import { HttpErrorResponse } from '@angular/common/http';
import {
  computed,
  DestroyRef,
  inject,
  Injectable,
  InjectionToken,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, FormRecord, Validators } from '@angular/forms';
import {
  asyncScheduler,
  debounceTime,
  map,
  merge,
  Observable,
  observeOn,
  startWith,
  Subscription,
  tap,
} from 'rxjs';

import { FormComponent } from '@plugins/client/forms/form';
import {
  GlobalSave,
  GlobalSaveService,
} from '@plugins/client/services/global-save.service';
import { ToastService } from '@plugins/client/toast/toast.service';
import { Maybe, MultilanguageContent } from '@plugins/shared/types';
import { isNumber, isObject } from '@plugins/shared/utils/assertFunctions';

import { MultilanguageService } from '../multilanguage.service';
import { MULTILANGUAGE_ERROR_KEY, multilanguageErrors } from '../validators';

import { MultilanguageFormShape } from './types';

export type MultilangaugeFormConfig = {
  /** Should the form be marked as pristine if its current value is equal
   * to its original value?
   *
   * Monitoring the form introduces overhead. See the `observeForm` method for more details. Defaults to `false`*/
  usePristineIfUnchanged?: boolean;
  /** How long the toast notification should be visible. Defaults to 5 seconds. */
  toastCloseTimeout?: number;
  /** Should toast notifications be shown after (un)successful
   * saves? Defaults to `true`*/
  showNotifications?: boolean;
};
export const MultilanguageFormConfigToken =
  new InjectionToken<MultilangaugeFormConfig>('MultilanguageFormConfig');

/** Template service that facilitates handling of forms with multilanguage form controls.
 * Typical use case is in backoffice form use-cases where forms are used in conjuction with
 * the `MultilanguageBar` that triggers add and remove language actions. Also allows
 * `formRef` registration if used together with `FormComponent`.
 *
 * Subclassed service needs to provide:
 *  - Appropriate form and data source generics
 * - form group builder method and source data getters, save callbacks and toast messages
 * - `multilanguageControls` getter is also required.
 *
 * Based on provided handlers the service:
 * - Creates new or remove existing multilanguage controls when the `MultilanguageService` emits
 * appropriate events.
 * - Allows additional operations on initial build, save success and save error actions.
 * - Shows toast notifications on save actions (configurable).
 * - Can mark form as pristineif current value equal original ones (defaults to false).
 *
 */
@Injectable()
export abstract class MultilanguageFormService<
  /** Previously, evaluating the generic against a `FormGroup` resulted in type errors
   * requiring us to compare the actual form group shape.
   * To infer the shape, the `GetFormShape<FormGroup>` helper can be used.  */
  GroupShape extends MultilanguageFormShape,
  Source extends { languages: string[] },
> {
  /** Source from which the data for the form
   * is retrieved. Usually this will be the plugin's instance. */
  abstract get data(): Source;
  /** Callback that sends form data to the server and
   * which return a a raw value observable that can be used to build
   * the form is returned. */
  abstract saveHandler(): Observable<Source>;
  /** Create implementation-specific form group.
   * Multilanguage validator will be added automatically */
  abstract createFormGroup(data: Source): FormGroup<GroupShape>;
  /** Return all multilanguage controls present in the current form.
   * These controls will be updated automatically upon language add or removal. */
  protected abstract get multilanguageControls(): FormRecord<
    FormControl<string>
  >[];

  /** Transform source data to a patchable object shape with which the form can be updated.
   * Called after successful save.*/
  abstract transformToPatchable(
    data: Source,
  ): Parameters<typeof this.form.patchValue>[0];

  /** Any additional actions that should be performed immediately after initial form
   * initialization should be placed here. */
  protected afterInitialHook(): void {}

  /** Any actions that should be performed after the form
   * has been saved should be placed here. */
  protected afterSuccessHook(data: Source): void {}
  /** Any actions that should be performed when form saving fails should be placed here. */
  protected afterErrorHook(err: unknown) {}

  protected readonly multilanguageService = inject(MultilanguageService);
  protected readonly globalSaveService = inject(GlobalSaveService);
  protected readonly toastService = inject(ToastService);

  protected destroyRef = inject(DestroyRef);
  protected config = inject(MultilanguageFormConfigToken, { optional: true });
  protected _usePristineIfUnchanged =
    this.config?.usePristineIfUnchanged ?? false;
  protected _toastCloseTimeout = this.config?.toastCloseTimeout ?? 5000;
  protected _showNotifications = this.config?.showNotifications ?? true;
  protected _form: FormGroup<GroupShape> | null = null;
  protected readonly _saving = signal(false);

  protected multiLangErrorsSub: Subscription | undefined;
  protected initialized = false;
  readonly saving = computed(() => this._saving());

  /**  If form observation is enabled, it will get (re)set on each
   * form build or save. */
  protected originalFormValue: ReturnType<
    FormGroup<GroupShape>['getRawValue']
  > | null = null;
  protected formObserver: Subscription | undefined;

  /** Messages that the toast service will display. */
  abstract readonly messages: {
    invalidMultilanguage: string;
    success: string;
    /** Message that should be shown if the error emitted on failed save
     * could not be parsed (i.e. is not Error or HttpErrorResponse type) */
    failureFallback: string;
  };

  get usePristineIfUnchanged() {
    return this._usePristineIfUnchanged;
  }
  set usePristineIfUnchanged(val: boolean) {
    this._usePristineIfUnchanged = val;
  }

  get toastCloseTimeout() {
    return this._toastCloseTimeout;
  }
  set toastCloseTimeout(val: number) {
    this._toastCloseTimeout = val;
  }

  get showNotifications() {
    return this._showNotifications;
  }
  set showNotifications(val: boolean) {
    this._showNotifications = val;
  }

  /** Get existing form, or initialize it, if it does not exist. */
  get form() {
    if (this._form) return this._form;
    return this.initialize();
  }

  set form(val: FormGroup<GroupShape>) {
    this._form = val;
  }

  get globalSave(): boolean {
    return this.globalSaveService.globalSave;
  }
  get currentMultilanguage$(): Observable<string> {
    return this.multilanguageService.currentLanguage$;
  }

  /*-- FormComponent related properties and methods --*/
  formRef?: Maybe<FormComponent<Source>>;
  addFormRef(formRef: FormComponent<Source>) {
    this.formRef = formRef;
  }

  removeFormRef() {
    this.formRef = null;
  }
  /*------*/

  get languageControls() {
    return (
      this.form.controls.languages as FormControl<string[]>
    ).getRawValue();
  }
  set languageControls(languages: string[]) {
    this.form.controls.languages.patchValue(languages);
  }
  /** Build form, register save handlers, multilanguage service observers, and optional form observers.
   *
   * Can also be invoked automatically by calling the `form` getter. */
  initialize() {
    const form = this.buildForm();
    if (this.initialized) {
      console.warn('Initialize has already ran, use build form instead.');
      return form;
    }
    this.initialized = true;
    this.afterInitialHook();
    this.globalSaveService.addSaveHandler(this.save);
    merge(
      this.multilanguageService.addLanguage$.pipe(
        tap(({ language, base }) => {
          this.addLanguage(language, base);
        }),
      ),
      this.multilanguageService.removeLanguage$.pipe(
        tap((language) => {
          this.removeLanguage(language);
        }),
      ),
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
    if (this.usePristineIfUnchanged) {
      this.observeForm();
    }
    return form;
  }

  /** Create language control from a passed value. If
   * value is not defined, empty strings will be assigned for
   * each of the available languages that are registered
   * in the `MultilanguageService`. */
  createLanguageControl({
    value,
    required = true,
  }: {
    value?: MultilanguageContent<string>;
    /** Defaults to true, as ordinarily multilanguage
     * entries need to be filled. */
    required?: boolean;
  }) {
    return this.multilanguageService.createLanguageControl<string>(
      value ?? this.createDefaultControl(),
      required ? Validators.required : undefined,
    );
  }

  protected createDefaultControl(): MultilanguageContent<string> {
    const languages = this.multilanguageService.languages;
    if (!languages) return {} as MultilanguageContent<string>;
    return languages.added.reduce<MultilanguageContent<string>>(
      (controls, lang) => {
        controls[lang] = '';
        return controls;
      },
      {},
    );
  }

  protected notify(
    message: string,
    color: string,
    closeTimeout = this.toastCloseTimeout,
  ): void {
    if (!this.showNotifications) return;
    this.toastService.show({ color, message, closeTimeout });
  }

  protected buildForm(data = this.data) {
    this.multilanguageService.setLimitedLanguages(data.languages);
    const form = this.createFormGroup(data);
    form.addValidators(
      multilanguageErrors(this.multilanguageService.availableLanguages),
    );
    this.form = form;
    this.setMultilanguageErrors();
    return this.form;
  }

  protected setMultilanguageErrors(): void {
    this.multiLangErrorsSub?.unsubscribe();
    this.multiLangErrorsSub = this.form.events
      .pipe(
        startWith(null),
        observeOn(asyncScheduler),
        tap(() => {
          this.multilanguageService.errors =
            (this.form.getError(MULTILANGUAGE_ERROR_KEY) as string[] | null) ??
            [];
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  save = (done?: GlobalSave) => {
    if (!this.form.dirty) {
      done?.(true);
      return;
    }
    if (this.form.disabled) {
      done?.(false);
      return;
    }
    if (!this.form.valid) {
      this.validateAndGetMultilanguageErrors();
      done?.(false);
      return;
    }
    this._saving.set(true);
    this.saveHandler().subscribe({
      next: (data) => {
        this._saving.set(false);
        this.form.patchValue(this.transformToPatchable(data));
        this.form.markAsPristine();
        if (this.usePristineIfUnchanged) {
          this.observeForm();
        }
        this.notify(this.messages.success, 'green');
        this.afterSuccessHook(data);
        done?.(true);
      },
      error: (err) => {
        this._saving.set(false);
        this.notify(this.handleResponseError(err), 'red');
        this.afterErrorHook(err);
        done?.(false);
      },
    });
  };

  resetForm() {
    if (!(this.usePristineIfUnchanged && this.originalFormValue)) return;
    this.buildForm(this.data);
  }

  handleResponseError(err: unknown): string {
    if (!(err instanceof Error || err instanceof HttpErrorResponse)) {
      return this.messages.failureFallback;
    }
    if (
      err instanceof HttpErrorResponse &&
      isObject(err.error) &&
      typeof err.error['message'] === 'string' &&
      isNumber(err.error['statusCode']) &&
      typeof err.error['error'] === 'string'
    ) {
      const { error, message, statusCode } = err.error;
      return `${error} (${statusCode}): ${message}`;
    }
    return err.message;
  }

  protected validateAndGetMultilanguageErrors(): void {
    const multilangError = this.form.errors?.[MULTILANGUAGE_ERROR_KEY] as
      | string[]
      | null;
    if (
      multilangError &&
      !multilangError.includes(this.multilanguageService.currentLanguage)
    ) {
      this.toastService.show({
        color: 'red',
        message: this.messages.invalidMultilanguage,
        closeTimeout: this.toastCloseTimeout,
      });
    }
  }

  protected addLanguage(language: string, base: string | undefined): void {
    this.multilanguageControls.forEach((control) => {
      // Each additional multilanguage control should
      // have the same validators as the previous ones.
      // We might consider moving this fallback to the
      // Multilanguage service.
      const existingValidator =
        control.controls[this.languageControls[0]].validator ?? undefined;
      this.multilanguageService.addLanguageFormControl(
        control,
        language,
        base,
        existingValidator,
      );
    });
    this.languageControls = [...this.languageControls, language];
    this.multilanguageService.addLanguage(language);
    this.form.markAsDirty();
  }

  protected removeLanguage(language: string): void {
    this.multilanguageControls.forEach((control) => {
      this.multilanguageService.removeLanguageFormControl(control, language);
    });
    this.languageControls = this.languageControls.filter(
      (lang) => lang !== language,
    );
    this.multilanguageService.removeLanguage(language);
    this.form.markAsDirty();
  }

  protected observeForm() {
    this.originalFormValue = this.form.getRawValue();
    this.formObserver?.unsubscribe();
    this.formObserver = this.form.valueChanges
      .pipe(
        debounceTime(50),
        map(
          () =>
            JSON.stringify(this.form.getRawValue()) ===
            JSON.stringify(this.originalFormValue),
        ),
        tap((isEqual) => {
          if (!isEqual) return;
          this.form.markAsPristine();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }
}
